import { Alert, Button, Divider, Form, Input, message, Radio, Spin } from 'antd';
import RadioCards from 'components/RadioCards/RadioCards';
import { Banner } from 'components/ui/Banner';
import { useCallback, useEffect, useState } from 'react';
import { familyService } from 'services';
import { FAMILY_APPLICATION_STEPS, FAMILY_BRANCH_MAP } from '../../familyApplicationConstants';
import { ChevronDown, ChevronUp } from '@carbon/icons-react';
import { useAsync } from 'react-async-hook';
import PageMeta from 'components/PageMeta/PageMeta';
import { useDispatch, useSelector } from 'react-redux';
import { actions, selectors } from 'features/auth';
import { getNextStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { NAME_REGEX } from 'constants';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export const MILITARY_SERVICE_OPTIONS = {
  SELF_ONLY: 'SelfOnly', // Just me
  SPOUSE_ONLY: 'SpouseOnly', // Just my spouse/partner
  BOTH: 'Both', // Both of us (dual military)
};

const DESIGNATED_SPONSOR_OPTIONS = {
  SELF: 'Self', // Mine
  SPOUSE: 'Spouse', // My spouse's/partner's
};

export function FamilyApplicationStart({ application, setApplication, household, loading, setStep, readOnly = false }) {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectors.selectUser);
  const stepConfig = useAtomValue(stepConfigAtom);

  const [form] = Form.useForm();
  const militaryServiceFormValue = Form.useWatch('militaryService', form);
  const designatedSponsorFormValue = Form.useWatch('designatedSponsor', form);

  // Determine if user is the military sponsor (isMilitarySponsor: true for "Just me" and dual military "Mine")
  const isMilitarySponsor =
    militaryServiceFormValue === MILITARY_SERVICE_OPTIONS.SELF_ONLY ||
    (militaryServiceFormValue === MILITARY_SERVICE_OPTIONS.BOTH &&
      designatedSponsorFormValue === DESIGNATED_SPONSOR_OPTIONS.SELF);

  // Determine if this is a dual military scenario
  const isDualMilitary = militaryServiceFormValue === MILITARY_SERVICE_OPTIONS.BOTH;

  // Determine if we should show the basic information section
  const shouldShowBasicInfo = militaryServiceFormValue && (!isDualMilitary || designatedSponsorFormValue);

  // State
  const [submitting, setSubmitting] = useState(false);

  // API fetches
  const { result: suffixOptions = [] } = useAsync(async () => {
    return familyService.getOptions('/options/20', { cache: { interpretHeader: false } });
  }, []);

  const onSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Use getNextStep to determine the next step
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.START, _newApplication, stepConfig);
        setStep({
          applicationId: _newApplication?.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);

        return;
      }

      try {
        setSubmitting(true);

        // FAMILY-APPS__DATA_CLEANING - Check if sponsor's firstName or lastName changed
        const sponsorNameChanged =
          application?.applicant?.firstName !== values.firstName ||
          application?.applicant?.lastName !== values.lastName;

        const _newSponsorData = await familyService.addUpdateAdult(
          household?.id,
          application?.applicant?.id ?? null,
          application?.applicant?.id !== undefined
            ? { ...application?.applicant, ...values, id: application?.applicant?.id }
            : [{ ...values, militaryDetails: { branch: { id: FAMILY_BRANCH_MAP.army.id } } }],
          isMilitarySponsor,
        );

        const _newApplication = await familyService.saveApplication({
          ...application,
          completedBySponsor: isMilitarySponsor,
          serviceMember: values.militaryService,
          designatedSponsor: values.designatedSponsor,
          isMilitarySponsor: isMilitarySponsor,
          // TODO: remove this once we support multiple program types
          programType: {
            id: 1,
          },
          // FAMILY-APPS__DATA_CLEANING - Clear eligibility certification if sponsor's name changes
          ...(sponsorNameChanged && {
            selfCertCompletedBySponsor: null,
            selfcertAccepted: null,
            selfcertAcceptedBy: null,
          }),
        });

        // Assign the applicant if not yet assigned
        if (application?.applicant?.id === undefined) {
          await familyService.addApplicant(_newApplication?.id, _newSponsorData?.[0]);
        }

        const mergedApplication = {
          ..._newApplication,
          applicant: {
            ...application?.applicant,
            ...(application?.applicant?.id !== undefined ? _newSponsorData : _newSponsorData?.[0]),
          },
        };

        setApplication(mergedApplication);

        if (isMilitarySponsor) {
          dispatch(actions.updateUser({ firstName: values.firstName, lastName: values.lastName }));
        }

        _goToNextStep(mergedApplication);
      } catch (error) {
        message.error('Something went wrong');
        newrelic.noticeError(error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, dispatch, household?.id, readOnly, setApplication, setStep, stepConfig, isMilitarySponsor],
  );

  // Suffix radio options
  const [showAllSuffixOptions, setShowAllSuffixOptions] = useState(false);

  useEffect(() => {
    if (application?.id !== undefined) {
      form.setFieldsValue({
        militaryService: application.serviceMember,
        designatedSponsor: application.designatedSponsor,
        firstName: application?.applicant?.firstName,
        middleName: application?.applicant?.middleName,
        lastName: application?.applicant?.lastName,
        suffix: application?.applicant?.suffix?.id,
      });

      if (application?.applicant?.suffix?.sequenceNumber > 6) {
        setShowAllSuffixOptions(true);
      }
    }
  }, [application, form]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <>
      <PageMeta title="Family application" />

      <Form form={form} layout="vertical" onFinish={onSubmit} disabled={readOnly}>
        <div className="p-7">
          <Banner
            question="Who in your household is currently serving or working for the Department of Defense (DoD)?"
            className="mb-12"
          />

          <Form.Item
            name="militaryService"
            rules={[
              {
                required: true,
                message: 'Please select an option',
              },
            ]}
            className="m-0 p-0"
          >
            <RadioCards
              data-testid="military-service-options"
              direction="vertical"
              options={[
                {
                  value: MILITARY_SERVICE_OPTIONS.SELF_ONLY,
                  label: 'I am',
                  'data-testid': 'self-only-option',
                },
                {
                  value: MILITARY_SERVICE_OPTIONS.SPOUSE_ONLY,
                  label: 'My spouse/domestic partner',
                  'data-testid': 'spouse-only-option',
                },
                {
                  value: MILITARY_SERVICE_OPTIONS.BOTH,
                  label: 'Both of us (dual military)',
                  'data-testid': 'both-dual-option',
                },
              ]}
              onChange={(_newValue) => {
                // Clear designated sponsor when military service changes
                form.setFieldsValue({
                  designatedSponsor: null,
                });

                // Auto-fill if "Just me" is selected and we have user info
                if (_newValue === MILITARY_SERVICE_OPTIONS.SELF_ONLY && currentUser) {
                  form.setFieldsValue({
                    firstName: currentUser?.firstName,
                    middleName: currentUser?.middleName,
                    lastName: currentUser?.lastName,
                    suffix: null,
                  });
                } else {
                  // Reset basic info fields for other selections
                  form.setFieldsValue({
                    firstName: null,
                    middleName: null,
                    lastName: null,
                    suffix: null,
                  });
                }

                setShowAllSuffixOptions(false);
              }}
            />
          </Form.Item>
        </div>

        {isDualMilitary && (
          <div className="p-7 pt-0" data-testid="dual-military-section">
            <Alert
              showIcon
              message="Since you're both military, the application needs to be in one person's name."
              description={
                <div className="mt-2">
                  <p className="mb-2">This is usually the person who:</p>
                  <ul className="mb-0 pl-4 list-disc">
                    <li>Has the higher rank</li>
                    <li>Is in the Army</li>
                    <li>Is currently activated (Guard/Reserve)</li>
                  </ul>
                </div>
              }
              className="mb-6"
              type="info"
            />

            <Banner
              variant="inline"
              question="Whose military service should we use for this application?"
              className="mb-6"
            />

            <Form.Item
              name="designatedSponsor"
              rules={[
                {
                  required: true,
                  message: 'Please select whose military service to use',
                },
              ]}
              className="m-0 p-0"
            >
              <RadioCards
                data-testid="designated-sponsor-options"
                direction="horizontal"
                options={[
                  {
                    value: DESIGNATED_SPONSOR_OPTIONS.SELF,
                    label: 'Mine',
                    'data-testid': 'self-sponsor-option',
                  },
                  {
                    value: DESIGNATED_SPONSOR_OPTIONS.SPOUSE,
                    label: "My spouse's/partner's",
                    'data-testid': 'spouse-sponsor-option',
                  },
                ]}
                onChange={(_newValue) => {
                  // Reset basic info fields when designated sponsor changes
                  form.setFieldsValue({
                    firstName: null,
                    middleName: null,
                    lastName: null,
                    suffix: null,
                  });

                  // Auto-fill if user selects themselves and we have their info
                  if (_newValue === DESIGNATED_SPONSOR_OPTIONS.SELF && currentUser) {
                    form.setFieldsValue({
                      firstName: currentUser?.firstName,
                      middleName: currentUser?.middleName,
                      lastName: currentUser?.lastName,
                      suffix: null,
                    });
                  }

                  setShowAllSuffixOptions(false);
                }}
              />
            </Form.Item>
          </div>
        )}

        {shouldShowBasicInfo && (
          <>
            <Divider className="mb-12 mt-6" />

            <div className="p-7 pt-0">
              <Banner
                variant="inline"
                question={
                  isMilitarySponsor
                    ? "Let's start with your basic information"
                    : 'Let’s start with their basic information'
                }
                className="mb-12"
              />

              <Form.Item
                name={['firstName']}
                label={isMilitarySponsor ? 'First name' : 'Their first name'}
                rules={[
                  { required: true, message: 'First Name is required' },
                  {
                    pattern: NAME_REGEX,
                    message: 'Invalid characters in the first name',
                  },
                ]}
                className="max-w-[600px] mb-0"
              >
                <Input autoComplete="off" data-testid="firstName" />
              </Form.Item>

              <Form.Item
                name={['middleName']}
                label={isMilitarySponsor ? 'Middle name' : 'Their middle name'}
                rules={[
                  {
                    pattern: NAME_REGEX,
                    message: 'Invalid characters in the middle name',
                  },
                ]}
                className="max-w-[600px] mt-8"
              >
                <Input autoComplete="off" data-testid="middleName" />
              </Form.Item>

              <Form.Item
                name={['lastName']}
                label={isMilitarySponsor ? 'Last name' : 'Their last name'}
                rules={[
                  { required: true, message: 'Last Name is required' },
                  {
                    pattern: NAME_REGEX,
                    message: 'Invalid characters in the last name',
                  },
                ]}
                className="max-w-[600px] mb-0"
              >
                <Input autoComplete="off" data-testid="lastName" />
              </Form.Item>

              <Form.Item
                name="suffix"
                label={isMilitarySponsor ? 'Suffix' : 'Their suffix'}
                className="max-w-[800px] mt-8 mb-0"
              >
                <Radio.Group
                  block
                  optionType="button"
                  buttonStyle="solid"
                  className="flex flex-wrap"
                  data-testid="suffix-options"
                >
                  {/* Show first 6 buttons */}
                  {suffixOptions.slice(0, 6).map((option) => (
                    <Radio.Button
                      key={option.id}
                      value={option.id}
                      className="flex-[unset] min-w-[90px]"
                      data-testid={`suffix-${option.id}`}
                    >
                      {option.id === 263 ? 'None' : option.title}
                    </Radio.Button>
                  ))}

                  {/* Additional buttons revealed on More click */}
                  {showAllSuffixOptions &&
                    suffixOptions.slice(6).map((option) => (
                      <Radio.Button
                        key={option.id}
                        value={option.id}
                        className="flex-[unset] min-w-[90px]"
                        data-testid={`suffix-${option.id}`}
                      >
                        {option.id === 263 ? 'None' : option.title}
                      </Radio.Button>
                    ))}

                  {/* More button */}
                  {suffixOptions.length > 6 && (
                    <span className="ant-radio-button-wrapper flex-[unset] min-w-[90px]">
                      <Button
                        type="text"
                        onClick={() => setShowAllSuffixOptions(!showAllSuffixOptions)}
                        className="w-full !h-[44px] rounded-[10px] border-solid border-[#cbd5e1] hover:border-[#6d7fff]"
                        data-testid="suffix-more-button"
                      >
                        {showAllSuffixOptions ? 'Less' : 'More'}
                        {showAllSuffixOptions ? <ChevronUp className="ml-[-2px] mr-[-2px]" /> : <ChevronDown />}
                      </Button>
                    </span>
                  )}
                </Radio.Group>
              </Form.Item>
            </div>
          </>
        )}

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{ disabled: true }}
          nextButtonProps={{
            loading: submitting,
          }}
        />
      </Form>
    </>
  );
}
